import { ReactNode } from "react";
import OpenEndedActivityMaterialContent from "./material-content/OpenEndedActivityMaterialContent";
import {
  ActivityBodyContent,
  parseContentMapFromData,
  parseBodyMapFromData,
} from "../../../library";
import ActivitySolveLayout from "../../../library/components/activities/ActivitySolveLayout";
import { blockCopyPasteEvent } from "./openEndedCopyGuard";

interface OpenEndedActivityContentProps {
  answerMap: any;
  data: any;
  canAnswerQuestion?: any;
  changeAnswer: (answerMap: any) => void;
  userId?: any;
  userProfileId?: any;
  // Generic upload source for surfaces that are neither a corporate nor a
  // personalized assignment (e.g. SUPPORT_ASSIGNMENT) — see resolveOpenEndedSource.
  sourceType?: string;
  sourceId?: any;
  branchAssignmentId?: any;
  personalizedAssignmentId?: any;
  activityId?: any;
  activityTemplateId?: any;
  curriculumType?: any;
  showMaterialContent?: boolean;
  webSocketStompClient?: any;
  // Same contract as the 8 library wrappers (IOpenEndedActivityProps):
  passage?: ReactNode;
  isPreview?: boolean;
  isFullScreen?: boolean;
  // Opt-in: read-only WITHOUT the graded (isPreview) card — e.g. an unanswered question of an ended destek
  // ödevi. Freezes the textarea and hides every upload/record tile. Callers that omit it are unchanged.
  isLocked?: boolean;
}

// OPEN_ENDED wrapper — same ActivitySolveLayout card as the other 8 templates
// (design: wide/single-column), so the passage renders in the question pane and
// the graded (isPreview) state freezes the answer like everywhere else.
const OpenEndedActivityContent = (props: OpenEndedActivityContentProps) => {
  const {
    answerMap,
    data,
    canAnswerQuestion,
    changeAnswer,
    userId,
    userProfileId,
    sourceType,
    sourceId,
    branchAssignmentId,
    personalizedAssignmentId,
    activityId,
    activityTemplateId,
    curriculumType,
    showMaterialContent,
    webSocketStompClient,
    passage,
    isPreview,
    isFullScreen,
    isLocked,
  } = props;
  const contentMap = parseContentMapFromData(data);
  const openEndedBodyMap = parseBodyMapFromData(data, "OPEN_ENDED");

  const handleOpenEndedAnswerOnChange = (answerMap: any, value: any) => {
    if (isPreview || isLocked) return;
    answerMap["ANSWER"] = value;
    changeAnswer(answerMap);
  };

  return (
    <ActivitySolveLayout
      variant="wide"
      // Copy/cut off across the whole card (passage + stem + answer pane) while the
      // question is being SOLVED, so the prompt can't be lifted out to an external
      // model; the textarea blocks paste/drop for the other half of that loop. In
      // preview (the /practise review screens, ActivityPreviewByData) the learner is
      // reading back a graded answer — left copyable.
      copyGuard={isPreview ? undefined : blockCopyPasteEvent}
      // The strongest case for pinning: a long stem (image + passage-style text +
      // numbered prompts) beside a short answer box, so the CEVAP pane pins and the
      // learner keeps the textarea in view while scrolling the question.
      // Safe despite being an input: `.oe2-area` is a plain textarea with
      // `min-height: 200px; resize: vertical` and NO auto-grow, so typing scrolls
      // it internally and never changes the pane's height. The height changes that
      // do exist — dragging the resize handle, an upload thumbnail appearing, the
      // graded "answer submitted" card — are all discrete, one-off events that
      // re-measure once rather than jittering while the learner works.
      paneFit
      isFullScreen={isFullScreen}
      passage={passage}
      body={
        <ActivityBodyContent
          bodyMap={openEndedBodyMap}
          templateType={"OPEN_ENDED"}
        />
      }
      interaction={
        showMaterialContent ? (
          <OpenEndedActivityMaterialContent
            answerMap={answerMap}
            bodyMap={openEndedBodyMap}
            contentMap={contentMap}
            checkCanAnswerQuestion={canAnswerQuestion}
            onChange={handleOpenEndedAnswerOnChange}
            userId={userId}
            userProfileId={userProfileId}
            sourceType={sourceType}
            sourceId={sourceId}
            branchAssignmentId={branchAssignmentId}
            personalizedAssignmentId={personalizedAssignmentId}
            activityId={activityId}
            activityTemplateId={activityTemplateId}
            curriculumType={curriculumType}
            webSocketStompClient={webSocketStompClient}
            isPreview={isPreview}
            isLocked={isLocked}
          />
        ) : null
      }
    />
  );
};

export default OpenEndedActivityContent;
